import React from 'react';
import generatePaySlipPDF from './Generatepayslippdf';

const COLOR_MAP = {
  yellow:  { card: 'bg-yellow-50  border-yellow-200',  badge: 'bg-yellow-100  text-yellow-700',  btn: 'bg-yellow-500  hover:bg-yellow-600'  },
  rose:    { card: 'bg-rose-50    border-rose-200',    badge: 'bg-rose-100    text-rose-700',    btn: 'bg-rose-500    hover:bg-rose-600'    },
  teal:    { card: 'bg-teal-50    border-teal-200',    badge: 'bg-teal-100    text-teal-700',    btn: 'bg-teal-500    hover:bg-teal-600'    },
  pink:    { card: 'bg-pink-50    border-pink-200',    badge: 'bg-pink-100    text-pink-700',    btn: 'bg-pink-500    hover:bg-pink-600'    },
  lime:    { card: 'bg-lime-50    border-lime-200',    badge: 'bg-lime-100    text-lime-700',    btn: 'bg-lime-500    hover:bg-lime-600'    },
  brown:   { card: 'bg-amber-50   border-amber-200',   badge: 'bg-amber-100   text-amber-700',   btn: 'bg-amber-600   hover:bg-amber-700'   },
  red:     { card: 'bg-red-50     border-red-200',     badge: 'bg-red-100     text-red-700',     btn: 'bg-red-500     hover:bg-red-600'     },
  blue:    { card: 'bg-blue-50    border-blue-200',    badge: 'bg-blue-100    text-blue-700',    btn: 'bg-blue-500    hover:bg-blue-600'    },
  emerald: { card: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', btn: 'bg-emerald-500 hover:bg-emerald-600' },
  orange:  { card: 'bg-orange-50  border-orange-200',  badge: 'bg-orange-100  text-orange-700',  btn: 'bg-orange-500  hover:bg-orange-600'  },
  violet:  { card: 'bg-violet-50  border-violet-200',  badge: 'bg-violet-100  text-violet-700',  btn: 'bg-violet-500  hover:bg-violet-600'  },
  crimson: { card: 'bg-red-50     border-red-300',     badge: 'bg-red-100     text-red-800',     btn: 'bg-red-700     hover:bg-red-800'     },
};

const DEFAULT_COLORS = {
  card:  'bg-gray-50  border-gray-200',
  badge: 'bg-gray-100 text-gray-700',
  btn:   'bg-gray-500 hover:bg-gray-600',
};

const PaySlipCard = ({
  month,
  year,
  color = 'blue',
  isAvailable = true,
  companyName = '',
  companyAddress = '',
  companyLogoUrl = null,
  downloadIcon,
  payslipData = {},
}) => {
  // ✅ THIS LINE WAS MISSING — caused the 'colors is not defined' error
  const colors = COLOR_MAP[color] ?? DEFAULT_COLORS;

 const handleDownload = async () => {
  if (!isAvailable) return;

  // Convert logo URL → base64 via canvas (bypasses jsPDF image issues)
  let companyLogoBase64 = null;
  if (companyLogoUrl) {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // request CORS headers
      await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = rej;
        img.src = companyLogoUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      companyLogoBase64 = canvas.toDataURL('image/png');
    } catch {
      companyLogoBase64 = null; // fall back gracefully
    }
  }

  generatePaySlipPDF({
    companyName,
    companyAddress,
    companyLogoBase64, // ← pass base64, not URL
    month: month?.toUpperCase() ?? '',
    year: year ?? '',
    ...payslipData,
  });
};

  return (
    <div
      className={`
        relative flex flex-col justify-between
        rounded-2xl border p-5 shadow-sm
        transition-all duration-200
        ${isAvailable ? `${colors.card} hover:shadow-md` : 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed'}
      `}
    >
      {/* Month badge */}
      <div className="flex items-center justify-between mb-4">
        <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${colors.badge}`}>
          {month}
        </span>
        <span className="text-sm text-gray-400 font-medium">{year}</span>
      </div>

      {/* Title */}
      <div className="mb-5">
        <p className="text-lg font-bold text-neutral-800 leading-tight">
          {month} {year}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">Pay Slip</p>
      </div>

      {/* Download button */}
      <button
        onClick={handleDownload}
        disabled={!isAvailable}
        aria-label={`Download pay slip for ${month} ${year}`}
        className={`
          flex items-center justify-center gap-2
          w-full py-2 px-4 rounded-xl
          text-white text-sm font-semibold
          transition-colors duration-150
          ${isAvailable ? `${colors.btn} cursor-pointer` : 'bg-gray-300 cursor-not-allowed'}
        `}
      >
        {downloadIcon ? (
          <img src={downloadIcon} alt="" className="w-4 h-4 object-contain brightness-0 invert" />
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
            <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
          </svg>
        )}
        Download PDF
      </button>
    </div>
  );
};

export default PaySlipCard;